/* Material */
import Typo from '@mui/material/Typography'
import { useMemo } from 'react'

import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

import { green } from '@mui/material/colors'

/* Functions */
import formatEuro from 'src/functions/formatEuro'
import formatPercent from 'src/functions/formatPercent'
import { getCartLinePrice } from 'src/functions/cartPriceLock'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'

/* Types */
import { discountPrice } from 'src/shared/frontendTypes/product'
import { TypographyProps } from '@mui/material'
import type { ElementType } from 'react'

interface Props extends Omit<TypographyProps, 'component'> {
  price: number | discountPrice
  qty?: number
  className?: string
  component?: ElementType
  layout?: 'default' | 'detail'
  showTaxHint?: boolean
  discountBadgeVariant?: 'bubble' | 'flag' | 'inline'
  ignoreQuantityDiscount?: boolean
  onlyStock?: boolean | null
  stock?: number | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    discountPriceWrapper: {
      display: 'grid',
      gridTemplateAreas: `
        "s"
        "d"
      `,
      justifyItems: 'center',
      position: 'relative',
      rowGap: theme.spacing(.15),
      textAlign: 'center',
      width: '100%',
      minWidth: 0,
    },

    discountPriceWrapperInline: {
      alignItems: 'center',
      columnGap: theme.spacing(.75),
      gridTemplateAreas: `
        "p s"
        "p d"
      `,
      gridTemplateColumns: 'auto minmax(0, max-content)',
      justifyContent: 'center',
      justifyItems: 'end',
    },

    strikePrice: {
      color: theme.palette.text.primary,
      gridArea: 's',
      fontSize: 'small',
      textDecoration: 'line-through',
      textAlign: 'center',
    },

    discountPrice: {
      gridArea: 'd',
      color: green[700],
      fontWeight: 800,
    },

    price: {
      gridArea: 'd',
      color: green[700],
      fontWeight: 800,
    },

    percent: {
      gridArea: 'p',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || theme.palette.getContrastText(theme.palette.primary.main),
      fontWeight: 800,
      lineHeight: 1,
      padding: theme.spacing(0.5, 1),
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },

    percentFlag: {
      alignSelf: 'center',
      backgroundColor: green[700],
      borderRadius: 999,
      color: '#fff',
      display: 'inline-flex',
      fontSize: '.86rem',
      fontWeight: 800,
      gridArea: 'p',
      justifySelf: 'start',
      lineHeight: 1,
      padding: theme.spacing(.5, .9),
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        borderRadius: '0 999px 999px 0',
        fontSize: '.82rem',
        padding: theme.spacing(.5, .85, .5, .65),
      },
    },

    percentInline: {
      alignSelf: 'center',
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      color: theme.palette.primary.contrastText || theme.palette.getContrastText(theme.palette.primary.main),
      display: 'inline-flex',
      fontSize: '.95rem',
      fontWeight: 800,
      gridArea: 'p',
      justifySelf: 'center',
      lineHeight: 1,
      padding: theme.spacing(.45, .85),
      position: 'static',
      transform: 'none',
      whiteSpace: 'nowrap',
    },

    detailWrapper: {
      boxSizing: 'border-box',
      display: 'grid',
      gap: theme.spacing(.45),
      alignItems: 'start',
      justifyContent: 'end',
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'stretch',
      },
    },

    detailSolo: {
      boxSizing: 'border-box',
      display: 'grid',
      justifyItems: 'start',
      maxWidth: '100%',
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        justifyItems: 'center',
        textAlign: 'center',
      },
      [theme.breakpoints.up('sm')]: {
        justifyItems: 'end',
        textAlign: 'right',
      },
    },

    detailPrice: {
      color: `${green[700]} !important`,
      fontSize: 'clamp(2rem, 4vw, 3rem) !important',
      fontWeight: 400,
      lineHeight: '1.05 !important',
      letterSpacing: '0 !important',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'clamp(1.75rem, 8vw, 2.25rem) !important',
        textAlign: 'center !important',
      },
    },

    detailDiscountPriceSmall: {
      color: `${green[700]} !important`,
      fontSize: 'clamp(1.7rem, 3vw, 2.25rem) !important',
      fontWeight: 400,
      lineHeight: '1.05 !important',
      letterSpacing: '0 !important',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'clamp(1.45rem, 6vw, 1.9rem) !important',
        textAlign: 'center !important',
      },
    },

    detailTaxHint: {
      boxSizing: 'border-box',
      color: theme.palette.text.secondary,
      fontSize: '1rem',
      lineHeight: 1.25,
      marginTop: theme.spacing(.35),
      maxWidth: '100%',
      overflowWrap: 'anywhere',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.95rem',
        textAlign: 'center',
      },
    },

    detailSide: {
      boxSizing: 'border-box',
      display: 'grid',
      gap: theme.spacing(.45),
      justifyItems: 'end',
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        justifyItems: 'center',
        textAlign: 'center',
      },
    },

    detailPriceLine: {
      alignItems: 'flex-start',
      boxSizing: 'border-box',
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.4),
      justifyContent: 'flex-end',
      maxWidth: '100%',
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1),
        justifyContent: 'center',
      },
    },

    detailPercent: {
      backgroundColor: green[700],
      borderRadius: 999,
      color: '#fff',
      display: 'inline-flex',
      fontSize: '1rem',
      fontWeight: 800,
      lineHeight: 1,
      padding: theme.spacing(.55, 1.2),
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.95rem',
        padding: theme.spacing(.5, 1),
      },
    },

    detailStrikePrice: {
      color: theme.palette.text.primary,
      fontSize: '1.25rem',
      lineHeight: 1.1,
      paddingTop: theme.spacing(.35),
      textDecoration: 'line-through',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.05rem',
        paddingTop: theme.spacing(.25),
      },
    },
  })
)

const PriceDisplay: React.FC<Props> = ({
  price,
  qty = 1,
  className,
  layout = 'default',
  showTaxHint = false,
  discountBadgeVariant = 'bubble',
  ignoreQuantityDiscount = false,
  onlyStock,
  stock,
  ...rest
}) => {
  const classes = useStyles()
  const dateEnd = typeof price === 'number' ? undefined : price.dateEnd
  const discountDateActive = useDiscountDateActive(dateEnd)
  const cartPriceOptions = useMemo(() => ({
    ignoreQuantityDiscount: ignoreQuantityDiscount || !discountDateActive,
    onlyStock,
    stock,
  }), [discountDateActive, ignoreQuantityDiscount, onlyStock, stock])
  const taxHint = showTaxHint
    ? <div className={classes.detailTaxHint}>inkl. MwSt. zzgl. Versand</div>
    : null
  const discountTotal = typeof price === 'number'
    ? price * qty
    : getCartLinePrice(price, qty, cartPriceOptions)
  const discountTotalWithoutQuantityDiscount = typeof price === 'number'
    ? discountTotal
    : getCartLinePrice(price, qty, {
      ...cartPriceOptions,
      ignoreQuantityDiscount: true,
    })
  const regularTotal = typeof price === 'number'
    ? price * qty
    : price.regularPrice * qty
  const hasEffectiveDiscount = typeof price !== 'number'
    && discountTotal < regularTotal - 0.005
  const hasBasePriceDiscount = typeof price !== 'number'
    && discountTotalWithoutQuantityDiscount < regularTotal - 0.005
  const showPercent = price && typeof price !== 'number'
    ? price.showPercent !== false && hasBasePriceDiscount && regularTotal > 0
    : false

  if (layout === 'detail') {
    if (typeof price === 'number' || price.type === 'invisible' || !hasEffectiveDiscount) {
      return <div className={[classes.detailSolo, className].filter(Boolean).join(' ')}>
        <Typo {...rest} className={classes.detailPrice}>
          {formatEuro(discountTotal)} *¹
        </Typo>
        {taxHint}
      </div>
    }

    return <div className={[classes.detailWrapper, className].filter(Boolean).join(' ')}>
      <div className={classes.detailSide}>
        {showPercent && (
          <span className={classes.detailPercent}>
            {formatPercent(
              (regularTotal - discountTotalWithoutQuantityDiscount) / regularTotal * -1
            )}
          </span>
        )}
        <div className={classes.detailPriceLine}>
          <span className={classes.detailStrikePrice}>
            {formatEuro(regularTotal)} *¹
          </span>
          <Typo {...rest} className={classes.detailDiscountPriceSmall}>
            {formatEuro(discountTotal)} *¹
          </Typo>
        </div>
        {taxHint}
      </div>
    </div>
  }

  /* ++++++++++++++++++++++++++++++++++++++++++++++++++ */
  // Normaler Preis
  if (typeof price === 'number')
    return (
      <div className={className}>
        <Typo {...rest} className={classes.price}>
          {formatEuro(price * qty)} *¹
        </Typo>
      </div>
    )

  /* ++++++++++++++++++++++++++++++++++++++++++++++++++ */
  // Falls der Discount "invisible" ist,
  // dann den Preis wie bei einem normalen Preis anzeigen (ohne Streichpreis/Prozent)
  if (price.type === 'invisible' || !hasEffectiveDiscount)
    return (
      <div className={className}>
        <Typo {...rest} className={classes.price}>
          {formatEuro(discountTotal)} *¹
        </Typo>
      </div>
    )

  /* ++++++++++++++++++++++++++++++++++++++++++++++++++ */
  // Preis einer Rabatt-Aktion
  const wrapperClassName = [
    classes.discountPriceWrapper,
    discountBadgeVariant === 'inline' ? classes.discountPriceWrapperInline : null,
    className,
  ].filter(Boolean).join(' ')
  const percentClassName = discountBadgeVariant === 'flag'
    ? classes.percentFlag
    : discountBadgeVariant === 'inline'
      ? classes.percentInline
      : classes.percent

  return (
    <div className={wrapperClassName}>
      <Typo {...rest} className={classes.strikePrice}>
        {formatEuro(regularTotal)} *¹
      </Typo>

      <Typo {...rest} className={classes.discountPrice}>
        {formatEuro(discountTotal)} *¹
      </Typo>

      {showPercent && (
        <Typo
          {...rest}
          className={percentClassName}
        >
          {formatPercent(
            (regularTotal - discountTotalWithoutQuantityDiscount) / regularTotal * -1
          )}
        </Typo>
      )}
    </div>
  )
}

export default PriceDisplay
